import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import styles from './stats.module.css';
import { makeRequest } from '../../Utils';
import { userState } from '../../atoms';

export default function AdminStats() {
  const [statLabel, setStatLabel] = useState('');
  const [statValue, setStatValue] = useState('');
  const [revealed, setRevealed] = useState([]);
  const [stat, setStat] = useState('salesToday');
  const [facts, setFacts] = useState([]);
  const [avgBooksMonth, setAvgBooksMonth] = useState(0);
  const [color, setColor] = useState('#e53b3c');
  const user = useRecoilValue(userState);

  const stats = ['salesToday', 'inventoryCount', 'fact', 'salesMonth', 'averageBooksMonth', 'inventoryTitles', 'readyBePicked', 'instagram', 'fact', 'newsletterSubscribers', 'shippedCount'];

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getSalesToday = async () => {
    const data = await makeRequest(`admin-report?start=${new Date().toISOString().split('T')[0]}&end=${new Date().toISOString().split('T')[0]}&stat=sales`);
    setStatValue(data.length.toString());
  }

  const backgrounds = ['#e53b3c', '#00374b', '#b9cdcc', ' #af945f', '#97a583', '#efb539']

  const getInventoryStats = async (stat) => {
    let inventoryData = await makeRequest('inventory-summary');
    if (stat === 'inventoryTitles') {
      setStatValue(inventoryData.titles.toLocaleString());
    } else {
      setStatValue(inventoryData.count.toLocaleString());
    }
  }

  const getInstagramFollowers = async () => {
    const url = `https://www.instagram.com/asterism_books/`;

    try {
      const response = await fetch(url);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const metaTag = doc.querySelector('meta[property="og:description"]');
      if (!metaTag) throw new Error('Meta tag not found');

      const content = metaTag.getAttribute('content');;
      const match = content.match(/^([\d,]+) Followers/);

      if (match) {
        console.log(`Follower count: ${match[1]}`);
        return match[1];
      }
    } catch (err) {
      console.error('Error:', err.message);
    }

  }

  const getPickQueueCount = async () => {
    let data = await makeRequest('new-shipments', 'POST', { "page": 0, "per": 100, "query": { "status": "Unpicked" } });
    let count = 0;
    data.results.forEach(order => {
      order.shipments.forEach(shipment => {
        if (shipment.isMatch) {
          count++;
        }
      })
    })
    setStatValue(count.toLocaleString());
  }

  const getSalesMonth = async () => {
    // Sales for last 30 days
    const start = new Date();
    start.setDate(start.getDate() - 30);
    const data = await makeRequest(`admin-report?start=${start.toISOString().split('T')[0]}&end=${new Date().toISOString().split('T')[0]}&stat=sales`);
    setStatValue(data.length.toLocaleString());
    // Average books per day in last month
    let totalBooks = 0;
    data.forEach(order => {
      Object.values(order.items).forEach(item => {
        totalBooks += item.quantity;
      });
    });
    setAvgBooksMonth((totalBooks / 30).toFixed(2));
  }

  const getNewsletterSubscribers = async () => {
    let newsletter = await makeRequest('newsletter-count');
    if (newsletter.count) {
      setStatValue(newsletter.count.toLocaleString());
    }
  }

  useEffect(() => {
    getFacts();
  }, [])

  const getShippedCount = async () => {
    let shipped = await makeRequest('shipped-count');
    setStatValue(shipped.count.toLocaleString());
  }

  const getFacts = async () => {
    const data = await makeRequest('options?id=facts');
    setFacts(data.facts);
  }

  const getFunFact = async () => {
    const randomIndex = Math.floor(Math.random() * facts.length);
    let fact = facts[randomIndex];
    setStatValue(fact.text);
    setStatLabel('');
  }

  // Reveal each character in the statValue one by one
  useEffect(() => {
    const revealCharacters = async () => {
      for (let i = 0; i < statValue.length; i++) {
        await sleep(300);
        setRevealed((prev) => {
          const newRevealed = [...prev];
          newRevealed[i] = true;
          return newRevealed;
        });
      }
    };
    revealCharacters();
  }, [statValue]);

  // Load stat whenever "stat" changes
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (isMounted) {
        setRevealed(Array(statValue.length).fill(false));
        await sleep(300);
        setStatValue('');
        await loadStat(stat);
        // Wait 12 seconds before cycling to next stat
        setTimeout(() => {
          setStat((prevStat) => {
            const currentIndex = stats.indexOf(prevStat);
            const nextIndex = (currentIndex + 1) % stats.length;
            return stats[nextIndex];
          });
        }, 12000);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stat]);

  if (!user || !user.admin) {
    return (
      <div className='asterismGridItem'>
        <div>You do not have access to this page.</div>
      </div>
    );
  }

  // Helper to load stat and wait for reveal
  const loadStat = async (currentStat) => {
    // Change background color
    const newColor = backgrounds[Math.floor(Math.random() * backgrounds.length)];
    setColor(newColor);
    if (currentStat === 'salesToday') {
      setStatLabel('Sales Today');
      await getSalesToday();
    } else if (currentStat === 'salesMonth') {
      setStatLabel('Sales Last 30 Days');
      await getSalesMonth();
    } else if (currentStat === 'newsletterSubscribers') {
      setStatLabel('Newsletter Subscribers');
      await getNewsletterSubscribers();
    } else if (currentStat === 'shippedCount') {
      setStatLabel('Orders Shipped in Last 24 Hours');
      await getShippedCount();
    } else if (currentStat === 'fact') {
      await getFunFact();
    } else if (currentStat === 'inventoryCount') {
      setStatLabel('Total Inventory');
      await getInventoryStats('inventoryCount');
    } else if (currentStat === 'inventoryTitles') {
      setStatLabel('Unique Titles in Inventory');
      await getInventoryStats('inventoryTitles');
    } else if (currentStat === 'averageBooksMonth') {
      setStatLabel('Average Books Sold Per Day (Last 30 Days)');
      setStatValue(Math.round(avgBooksMonth).toString());
    } else if (currentStat === 'readyBePicked') {
      setStatLabel('Orders in Pick Queue');
      await getPickQueueCount();
    } else if (currentStat === 'instagram') {
      setStatLabel('Instagram Followers');
      let followers = await getInstagramFollowers();
      setStatValue(followers || '');
    }
  };


  return (
    <div className={styles.statsContainer} style={{ backgroundColor: color }}>
      <div className={styles.statBox}>
        <h2 className={styles.statLabel}>{statLabel}</h2>
        <div className={styles.statValue}>
          {stat !== 'fact' && statValue.split('').map((char, index) => (
            <span
              key={index}
              className={`${styles.char} ${revealed[index] ? styles.revealed : ''}`}
            >
              {char}
            </span>
          ))}
          {stat === 'fact' && (<span className={`${styles.factText} ${styles.fadeIn} ${styles.revealed}`}>{statValue}</span>
          )}
        </div>
      </div>
    </div>
  );
}